/**
 * Directors' Deadline Dashboard
 * Read-only firm-wide view of deadlines for partners and managers.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AlertTriangle, Clock, CheckCircle2, Shield, Users, ArrowLeft, Filter, Bell, Send } from "lucide-react";
import VouchingForID from "../../components/VouchingForID";

import { API } from '../../config/api';

const TYPE_LABELS = {
  court_tribunal: "Court / Tribunal",
  statutory: "Statutory",
  client_instruction: "Client instruction",
  internal: "Internal",
  other: "Other",
};

const TYPE_COLORS = {
  court_tribunal: "bg-status-red-dim/30 text-status-red border-red-700",
  statutory: "bg-status-amber-dim/30 text-status-amber border-amber-700",
  client_instruction: "bg-blue-900/30 text-blue-300 border-blue-700",
  internal: "bg-gray-800/50 text-gray-300 border-gray-600",
  other: "bg-gray-800/50 text-gray-300 border-gray-600",
};

export default function DeadlineDirectors() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ fee_earner: "", deadline_type: "", high_risk: "", date_range: "", status: "open" });
  const [reminders, setReminders] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [escalations, setEscalations] = useState(null);
  const [runningEscalations, setRunningEscalations] = useState(false);
  const [riskMatters, setRiskMatters] = useState({ high: [], medium: [] });
  const [loadingRisk, setLoadingRisk] = useState(false);

  const userId = localStorage.getItem("bonsai_user_id");

  useEffect(() => {
    if (!userId) { window.location.href = "/login"; }
  }, [userId]);

  async function fetchData() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const resp = await fetch(`${API}/bonsai/deadlines/directors/overview?${params}`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      const d = await resp.json();
      setData(d);
    } catch (e) {
      console.error("Failed to fetch director overview:", e);
    }
    setLoading(false);
  }

  useEffect(() => { fetchData(); }, [filters]);

  async function fetchReminders() {
    try {
      const resp = await fetch(`${API}/bonsai/deadlines/reminders/preview?hours_ahead=48`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      setReminders(await resp.json());
    } catch (e) { console.error(e); }
  }

  async function sendReminders() {
    setSendingReminders(true);
    try {
      await fetch(`${API}/bonsai/deadlines/reminders/send?hours_ahead=48`, {
        method: "POST",
        headers: { "X-Bonsai-User-Id": userId },
      });
      await fetchReminders();
    } catch (e) { console.error(e); }
    setSendingReminders(false);
  }

  useEffect(() => { fetchReminders(); }, []);

  // Fetch Risk Matters
  async function fetchRiskMatters() {
    setLoadingRisk(true);
    try {
      const [highRes, medRes] = await Promise.all([
        fetch(`${API}/risk/high-risk-matters?limit=20`, {
          headers: { "X-Bonsai-User-Id": userId },
        }),
        fetch(`${API}/risk/medium-risk-matters?limit=20`, {
          headers: { "X-Bonsai-User-Id": userId },
        })
      ]);
      const high = await highRes.json();
      const med = await medRes.json();
      setRiskMatters({
        high: high.high_risk_matters || [],
        medium: med.medium_risk_matters || []
      });
    } catch (e) { 
      console.error("Failed to fetch risk matters:", e); 
    }
    setLoadingRisk(false);
  }

  useEffect(() => { fetchRiskMatters(); }, []);

  async function fetchEscalations() {
    try {
      const resp = await fetch(`${API}/bonsai/deadlines/escalations/preview?overdue_hours=24`, {
        headers: { "X-Bonsai-User-Id": userId },
      });
      setEscalations(await resp.json());
    } catch (e) { console.error(e); }
  }

  async function runEscalations() {
    setRunningEscalations(true);
    try {
      await fetch(`${API}/bonsai/deadlines/escalations/run?overdue_hours=24`, {
        method: "POST",
        headers: { "X-Bonsai-User-Id": userId },
      });
      await fetchEscalations();
      fetchData();
    } catch (e) { console.error(e); }
    setRunningEscalations(false);
  }

  useEffect(() => { fetchEscalations(); }, []);

  const summary = data?.summary || {};
  const tasks = data?.tasks || [];
  const feBreakdown = data?.fee_earner_breakdown || [];

  const isOverdue = (t) => t.status === "open" && t.deadline_date < new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="deadline-directors-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold" data-testid="directors-title">Deadlines & Tasks — Directors' View</h1>
              <p className="text-sm text-gray-400">Firm-wide deadline oversight</p>
            </div>
          </div>
          <Badge variant="outline" className="text-gray-400 border-gray-700">
            <Shield className="w-3 h-3 mr-1" /> Read-only
          </Badge>
        </div>

        {/* Deadline Reminders (48h) */}
        {reminders && reminders.pending_reminders > 0 && (
          <Card className="bg-status-red-dim/20 border-red-800/50 mb-4" data-testid="reminders-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-status-red" />
                  <div>
                    <p className="text-sm font-medium text-status-red">
                      {reminders.pending_reminders} deadline{reminders.pending_reminders !== 1 ? "s" : ""} due within 48 hours
                    </p>
                    <p className="text-xs text-status-red/70 mt-0.5">
                      {reminders.deadlines.filter(d => !d.reminder_already_sent).map(d => d.summary).join(" · ")}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={sendReminders}
                  disabled={sendingReminders}
                  className="bg-status-red hover:bg-status-red text-white"
                  data-testid="send-reminders-btn"
                >
                  <Send className="w-3.5 h-3.5 mr-1" />
                  {sendingReminders ? "Sending..." : "Send reminders"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {reminders && reminders.pending_reminders === 0 && reminders.total > 0 && (
          <Card className="bg-status-green-dim/20 border-green-800/30 mb-4" data-testid="reminders-sent-card">
            <CardContent className="p-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-status-green" />
              <p className="text-xs text-status-green">All {reminders.total} upcoming deadline reminder{reminders.total !== 1 ? "s" : ""} sent</p>
            </CardContent>
          </Card>
        )}

        {/* Escalation Alerts */}
        {escalations && escalations.pending_escalations > 0 && (
          <Card className="bg-status-amber-dim/20 border-orange-800/50 mb-4" data-testid="escalations-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-status-amber" />
                  <div>
                    <p className="text-sm font-medium text-orange-300">
                      {escalations.pending_escalations} overdue deadline{escalations.pending_escalations !== 1 ? "s" : ""} need escalation
                    </p>
                    <p className="text-xs text-status-amber/70 mt-0.5">
                      {escalations.deadlines.filter(d => !d.already_escalated).map(d => `${d.summary} (${d.days_overdue}d overdue)`).join(" · ")}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={runEscalations}
                  disabled={runningEscalations}
                  className="bg-status-amber hover:bg-status-amber text-white"
                  data-testid="run-escalations-btn"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                  {runningEscalations ? "Escalating..." : "Escalate now"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        {escalations && escalations.pending_escalations === 0 && escalations.already_escalated > 0 && (
          <Card className="bg-gray-900/50 border-gray-700/30 mb-4" data-testid="escalations-done-card">
            <CardContent className="p-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-400">{escalations.already_escalated} overdue deadline{escalations.already_escalated !== 1 ? "s" : ""} already escalated to {escalations.escalation_target?.name || "partner"}</p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-white" data-testid="total-open">{summary.total_open || 0}</div>
              <div className="text-xs text-gray-400 mt-1">Open deadlines</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-red-900/50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-status-red" data-testid="total-overdue">{summary.overdue || 0}</div>
              <div className="text-xs text-status-red/70 mt-1">Overdue</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-amber-900/50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-status-amber" data-testid="due-this-week">{summary.due_this_week || 0}</div>
              <div className="text-xs text-status-amber/70 mt-1">Due this week</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-orange-900/50">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-status-amber" data-testid="high-risk-count">{summary.high_risk || 0}</div>
              <div className="text-xs text-status-amber/70 mt-1">High risk</div>
            </CardContent>
          </Card>
        </div>

        {/* Fee Earner Breakdown */}
        {feBreakdown.length > 0 && (
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-gray-300 flex items-center gap-1.5">
                <Users className="w-4 h-4" /> Fee Earner Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {feBreakdown.map((fe) => (
                  <button
                    key={fe.email}
                    onClick={() => setFilters(f => ({ ...f, fee_earner: f.fee_earner === fe.email ? "" : fe.email }))}
                    className={`text-left p-2 rounded border transition-colors ${
                      filters.fee_earner === fe.email ? "border-blue-500 bg-blue-900/20" : "border-gray-700 bg-gray-800/30 hover:bg-gray-800"
                    }`}
                    data-testid={`fe-card-${fe.email}`}
                  >
                    <div className="text-xs font-medium text-white truncate">{fe.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-bold text-white">{fe.total}</span>
                      {fe.overdue > 0 && <span className="text-[10px] text-status-red font-medium">{fe.overdue} overdue</span>}
                      {fe.high_risk > 0 && <span className="text-[10px] text-status-amber">{fe.high_risk} risk</span>}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Matters Section (SRA Compliance) */}
        {(riskMatters.high.length > 0 || riskMatters.medium.length > 0) && (
          <Card className="bg-gray-900 border-gray-800 mb-6" data-testid="risk-matters-section">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-gray-300 flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-status-amber" /> Risk Assessment Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-4">
              {/* High Risk Matters */}
              {riskMatters.high.length > 0 && (
                <div className="bg-status-red-dim/30 border border-red-800/50 rounded-md p-3" data-testid="high-risk-matters">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-status-red" />
                    <span className="text-sm font-medium text-status-red">High Risk Matters ({riskMatters.high.length})</span>
                    <span className="text-[10px] text-status-red/70">Supervisor review required</span>
                  </div>
                  <div className="space-y-1.5">
                    {riskMatters.high.slice(0, 5).map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-xs bg-status-red-dim/30 rounded px-2 py-1.5">
                        <div className="flex-1 min-w-0">
                          <span className="text-white font-medium">{m.client_name}</span>
                          <span className="text-status-red/80 mx-1">·</span>
                          <span className="text-status-red/70 truncate">{m.matter_title}</span>
                        </div>
                        <div className="flex items-center gap-2 ml-2 shrink-0">
                          <span className="text-status-red/70">{m.fee_earner}</span>
                          <Badge className="bg-status-red/50 text-status-red text-[10px] px-1.5">
                            Score: {m.total_score}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {riskMatters.high.length > 5 && (
                      <p className="text-[10px] text-status-red/60 text-right">+ {riskMatters.high.length - 5} more</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Medium Risk Matters */}
              {riskMatters.medium.length > 0 && (
                <div className="bg-status-amber-dim/20 border border-amber-800/30 rounded-md p-3" data-testid="medium-risk-matters">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-status-amber" />
                    <span className="text-sm font-medium text-status-amber">Medium Risk Matters ({riskMatters.medium.length})</span>
                    <span className="text-[10px] text-status-amber/70">Review recommended</span>
                  </div>
                  <div className="space-y-1">
                    {riskMatters.medium.slice(0, 3).map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-xs">
                        <div className="flex-1 min-w-0">
                          <span className="text-white">{m.client_name}</span>
                          <span className="text-status-amber/60 mx-1">·</span>
                          <span className="text-status-amber/60 truncate">{m.matter_title}</span>
                        </div>
                        <span className="text-status-amber/50 text-[10px] ml-2">{m.fee_earner}</span>
                      </div>
                    ))}
                    {riskMatters.medium.length > 3 && (
                      <p className="text-[10px] text-status-amber/50 text-right">+ {riskMatters.medium.length - 3} more</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vouching for ID — COLP/COFA */}
        <VouchingForID
          directorEmail={userId}
          directorName={userId}
        />

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="directors-filters">
          <Select value={filters.deadline_type} onValueChange={(v) => setFilters(f => ({ ...f, deadline_type: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-40 bg-gray-900 border-gray-700 text-sm h-8"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.date_range} onValueChange={(v) => setFilters(f => ({ ...f, date_range: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-36 bg-gray-900 border-gray-700 text-sm h-8"><SelectValue placeholder="All dates" /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="all">All dates</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="7days">Next 7 days</SelectItem>
              <SelectItem value="30days">Next 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.high_risk} onValueChange={(v) => setFilters(f => ({ ...f, high_risk: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-32 bg-gray-900 border-gray-700 text-sm h-8"><SelectValue placeholder="All risk" /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="all">All risk</SelectItem>
              <SelectItem value="true">High risk only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
            <SelectTrigger className="w-32 bg-gray-900 border-gray-700 text-sm h-8"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tasks Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">Loading deadlines...</div>
            ) : tasks.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No deadlines match the current filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="directors-table">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-400 text-xs">
                      <th className="px-4 py-2 text-left font-medium">Date</th>
                      <th className="px-4 py-2 text-left font-medium">Summary</th>
                      <th className="px-4 py-2 text-left font-medium">Matter</th>
                      <th className="px-4 py-2 text-left font-medium">Fee Earner</th>
                      <th className="px-4 py-2 text-left font-medium">Type</th>
                      <th className="px-4 py-2 text-center font-medium">Risk</th>
                      <th className="px-4 py-2 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((t) => (
                      <tr key={t.deadline_id} className={`border-b border-gray-800/50 hover:bg-gray-800/30 ${isOverdue(t) ? "bg-status-red-dim/10" : ""}`} data-testid="deadline-row">
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <span className={`text-xs font-mono ${isOverdue(t) ? "text-status-red font-bold" : "text-white"}`}>
                            {t.deadline_date}
                          </span>
                          {t.deadline_time && <span className="text-[10px] text-gray-500 ml-1">{t.deadline_time}</span>}
                        </td>
                        <td className="px-4 py-2.5 max-w-xs truncate text-xs text-white">{t.summary}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-300 font-mono">{t.matter_reference || "—"}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-300">{t.responsible_user_name || t.responsible_user_id || "—"}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TYPE_COLORS[t.deadline_type] || TYPE_COLORS.other}`}>
                            {TYPE_LABELS[t.deadline_type] || t.deadline_type}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {t.high_risk_flag && <AlertTriangle className="w-3.5 h-3.5 text-status-amber inline" />}
                        </td>
                        <td className="px-4 py-2.5">
                          {t.status === "completed" ? (
                            <Badge variant="outline" className="text-status-green border-green-700 text-[10px]">Done</Badge>
                          ) : isOverdue(t) ? (
                            <Badge variant="outline" className="text-status-red border-red-700 text-[10px]">Overdue</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400 border-gray-700 text-[10px]">Open</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="text-xs text-gray-500 mt-2 text-right">{data?.total || 0} deadline{(data?.total || 0) !== 1 ? "s" : ""}</div>
      </div>
    </div>
  );
}
